/* The model sheet, rebuilt as a split panel.

   WHAT THIS DOES. The extension renders one column: a big photograph, then the
   name, then a disclosure holding the identification facts, then a native year
   select with a dead Find Parts button beside it, then three accordions. This
   moves those same nodes into two columns, preselects the current year so the
   button is live on arrival, and adds the three figures the sheet never had.

   NOTHING IS RECREATED, EVERYTHING IS MOVED, so listeners come with the nodes.

   WHAT IS IN EACH COLUMN. Left is the trailer: render, name, the three
   figures, and the identification facts. Right is the decision: the year and
   the reference tables. Everything on the left answers one question, is this
   my trailer, and the right hand side is then only the things the customer has
   to do.

   THIS FILE RUNS ON A TIMER, SO IT MUST NOT WRITE WHEN NOTHING CHANGED.
   tick() runs every 800ms and again on every body mutation. fx-ext-vinchip.js
   watches the body too, and every mutation schedules it to re-measure and
   re-shrink-wrap the VIN selector row. So an unconditional write here is not
   a private cost: it is a layout and measure pass across the whole modal,
   twice a second, for as long as the page is open. syncFoot used to assign
   textContent every pass whether or not the text had changed, which is
   exactly that. Every write in the tick path is now guarded.

   THE SCHEDULER IS A TIMEOUT, NOT A FRAME. requestAnimationFrame does not run
   in a hidden or backgrounded tab, and it can be starved during heavy layout.
   When it did not run, the sheet opened as the extension's own single column
   markup and looked like a completely different component. A timeout does not
   care, and the slow interval underneath it catches anything the observer
   misses entirely.

   THE STAMP IS NOT PROOF, THE MARKUP IS. Every open re-renders the sheet from
   scratch: fresh top, fresh body, our grid discarded. The data-fx-ms attribute
   sits on .ident-sheet, which is the one node that survives, so trusting it
   meant the second open was never rebuilt and came up as the extension's raw
   single column with a render the height of the panel. intact() looks for the
   grid and its contents instead, and a sheet that fails goes back through
   build as if it had never been touched.

   THE ACCORDIONS ARRIVE LATE. They are rendered after the specification fetch
   returns, which is usually after the first rebuild, so build cannot rely on
   finding them. adoptSpecs runs on every pass and moves them in whenever they
   turn up, wherever the extension put them. */
(function () {
  'use strict';

  var SPECS_URL = '/apps/vin-search/modelSpecs';
  var ACC_MS = 240;
  var frame = 0;

  function norm(s) {
    return String(s == null ? '' : s).toUpperCase().replace(/[^A-Z0-9]+/g, ' ').trim();
  }
  function el(tag, cls, text) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text != null) n.textContent = text;
    return n;
  }
  function reducedMotion() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  /* ---- model specs, shared with the other scripts through one cached GET ---- */
  var specs = null;
  function loadSpecs(then) {
    if (specs) { then(); return; }
    if (window.fxSpecsPromise) { window.fxSpecsPromise.then(function (m) { specs = m; then(); }); return; }
    window.fxSpecsPromise = fetch(SPECS_URL, { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (j) {
        var map = {};
        ((j && j.specs) || []).forEach(function (m) {
          var row = {};
          (m.groups || []).forEach(function (g) {
            (g.rows || []).forEach(function (r) {
              var lab = String(r.label || '').toLowerCase();
              if (lab.indexOf('deck length') > -1) row.len = r.value;
              else if (lab.indexOf('deck width') > -1) row.wid = r.value;
              else if (lab.indexOf('load capacity') > -1) row.cap = r.value;
            });
          });
          map[norm(m.modelName)] = row;
        });
        return map;
      })
      .catch(function () { return {}; });
    window.fxSpecsPromise.then(function (m) { specs = m; then(); });
  }

  /* A qualifier belongs on the label, not inside a 30px figure. */
  function splitVal(raw) {
    var m = /^(.*?)\s*\(([^)]+)\)\s*$/.exec(String(raw || ''));
    return m ? { fig: m[1].trim(), note: m[2].trim() } : { fig: String(raw || ''), note: '' };
  }

  /* Imperial only. The spec sheet carries the metric figure in brackets on
     some models ("19'8\" (6.0 m)", "78 3/4\" (2000 mm)") and it was reaching
     the label as "Deck Length, 6.0 m". A note that is a measurement in metric
     units is dropped; any other note ("estimated") still goes on the label. */
  var METRIC_NOTE = /^[\d.,\s]+(?:m|mm|cm|kg|t)$/i;

  function figure(label, raw) {
    if (!raw) return null;
    var p = splitVal(raw);
    if (METRIC_NOTE.test(p.note)) p.note = '';
    var cell = el('div');
    cell.appendChild(document.createTextNode(p.note ? label + ', ' + p.note : label));
    var b = el('b', null, p.fig.replace(/(\d)'(\d)/g, "$1' $2"));
    cell.appendChild(b);
    return cell;
  }

  /* The line-up says "SHIFTR 1883" and the spec sheet files it as "SHIFTR",
     so an exact lookup left the SHIFTR sheet with no figures at all. Exact
     name first, then the longest spec name found in it as whole words, the
     same rule as fx-render.js and fx-ext-find.js. */
  function specsFor(name) {
    var k = norm(name);
    if (!k || !specs) return null;
    if (specs[k]) return specs[k];
    var hay = ' ' + k + ' ', best = '';
    for (var n in specs) {
      if (!Object.prototype.hasOwnProperty.call(specs, n)) continue;
      if (n && n.length > best.length && hay.indexOf(' ' + n + ' ') !== -1) best = n;
    }
    return best ? specs[best] : null;
  }

  function fillFigures(host, name) {
    if (!specs) { loadSpecs(function () { fillFigures(host, name); }); return; }
    var row = specsFor(name);
    if (!row) return;
    host.textContent = '';
    [figure('Deck Length', row.len), figure('Deck Width', row.wid), figure('Load Capacity', row.cap)]
      .forEach(function (c) { if (c) host.appendChild(c); });
  }

  /* ---- the year ------------------------------------------------------
     This was a row of chips. Seven years plus an escape hatch wrapped onto two
     lines and took more vertical space than the question and the identification
     panel put together. The extension's own select does the same job in one
     line and carries its own handler, so it is surfaced and styled rather than
     replaced (fx-sheet2.js puts the year buttons back on top of it).

     30 Sep: NO PRESELECT. The newest year used to be picked on arrival so Find
     Parts was live straight away, and most people never looked at the year.
     A wrong year is a wrong parts list, so the red button now stays grey until
     a year is chosen, which is the extension's own gate. */
  function buildYears(yearBlock) {
    var sel = yearBlock.querySelector('select.vin-filter-select');
    if (!sel) return;

    var host = yearBlock.querySelector('.ident-sheet-yearsel');
    if (host && host.hasAttribute('data-fx-hide')) host.removeAttribute('data-fx-hide');

    var chips = yearBlock.querySelector('.fx-ms__years');
    if (chips && chips.parentNode) chips.parentNode.removeChild(chips);

    if (yearBlock.getAttribute('data-fx-yr')) return;
    yearBlock.setAttribute('data-fx-yr', '1');
  }

  /* The accordions are the last thing in the right column on purpose: the full
     dimension tables are exactly what somebody scrolls for, and nothing above
     them should have to move out of the way first. */
  function adoptSpecs(sheet) {
    var scroll = sheet.querySelector('.fx-ms__scroll');
    var acc = sheet.querySelector('.ident-acc');
    if (!scroll || !acc || scroll.contains(acc)) return;
    var sp = scroll.querySelector('.fx-ms__specs');
    if (!sp) {
      sp = el('div', 'fx-ms__specs');
      sp.appendChild(el('h4', 'fx-ms__idh', 'Specifications'));
      scroll.appendChild(sp);
    }
    sp.appendChild(acc);
  }

  /* ---- the accordions open and close, rather than blinking -------------
     A details element has no animation of its own: the browser shows the whole
     panel in one frame, which in a sheet this size reads as the layout
     breaking rather than as something opening. This drives the height itself.

     ONE DELEGATED LISTENER ON THE SHEET, NOT ONE PER PANEL. The sheet's inner
     HTML is replaced wholesale on every open, and the accordions themselves
     are replaced again when the specification fetch returns, so anything bound
     to a panel has to be rebound and the guard that stops it being bound twice
     has to survive the same replacement. It does not. Two listeners on one
     summary is not a tidiness problem: the second call cancels the first
     call's animation, the cancelled animation never fires onfinish, and the
     panel is left open with the caret pointing the wrong way.

     SETTLE IS ON A TIMER AS WELL AS ON onfinish, AND THE TIMER IS THE ONLY
     GUARANTEE. A web animation can be cancelled or replaced by anything that
     touches the element, and a cancelled animation fires no onfinish.

     THE ELEMENT KEEPS ITS OWN SEMANTICS. The click is intercepted and open is
     set by hand, but this is still a real details with a real summary, so the
     keyboard, the accessibility tree and the extension's markup are untouched.
     Reduced motion falls through to the native instant toggle. */
  function wireAcc(sheet) {
    if (sheet.getAttribute('data-fx-acc')) return;
    sheet.setAttribute('data-fx-acc', '1');
    sheet.addEventListener('click', function (e) {
      var t = e.target;
      if (!t || !t.closest) return;
      var summary = t.closest('summary');
      if (!summary) return;
      var d = summary.closest('details');
      if (!d || !d.closest('.fx-ms__specs')) return;
      var panel = d.querySelector('.ident-kv');
      if (!panel || typeof panel.animate !== 'function' || reducedMotion()) return;
      e.preventDefault();
      slide(d, panel);
    });
  }

  function slide(d, panel) {
    if (panel.fxAnim) { try { panel.fxAnim.cancel(); } catch (err) {} }
    if (panel.fxTimer) { clearTimeout(panel.fxTimer); panel.fxTimer = 0; }

    var opening = !d.open;
    var from, to;

    if (opening) {
      d.open = true;
      from = 0;
      to = panel.getBoundingClientRect().height;
    } else {
      from = panel.getBoundingClientRect().height;
      to = 0;
    }

    if (!to && !from) { d.open = opening; return; }

    var anim = panel.animate(
      [
        { height: from + 'px', opacity: opening ? 0 : 1 },
        { height: to + 'px', opacity: opening ? 1 : 0 }
      ],
      { duration: ACC_MS, easing: 'cubic-bezier(.25, .1, .25, 1)' }
    );
    panel.fxAnim = anim;

    function settle() {
      if (panel.fxAnim !== anim) return;
      panel.fxAnim = null;
      if (panel.fxTimer) { clearTimeout(panel.fxTimer); panel.fxTimer = 0; }
      d.open = opening;
    }

    anim.onfinish = settle;
    panel.fxTimer = setTimeout(settle, ACC_MS + 90);
  }

  /* ---- the rebuild ---------------------------------------------------- */
  function build(sheet) {
    var top = sheet.querySelector('.ident-sheet-top');
    var body = sheet.querySelector('.ident-sheet-body');
    if (!top || !body) return;

    var head = body.querySelector('.ident-sheet-head');
    var h3 = head && head.querySelector('h3');
    var idbtn = head && head.querySelector('.ident-idbtn');
    var idpop = body.querySelector('.ident-idpop');
    var yearBlock = body.querySelector('.ident-sheet-year');
    var close = sheet.querySelector('.ident-sheet-close');
    var primary = yearBlock && yearBlock.querySelector('.is-primary');
    var quiet = yearBlock && yearBlock.querySelector('.is-quiet');
    if (!h3 || !yearBlock) return;

    var name = (h3.textContent || '').trim();

    var grid = el('div', 'fx-ms');
    var L = el('div', 'fx-ms__l');
    var R = el('div', 'fx-ms__r');
    grid.appendChild(L);
    grid.appendChild(R);

    /* left: everything that answers "is this my trailer". The well is sized to
       the trailer in fx-scale.css rather than to a fixed ratio, so the column
       centres on the render itself and not on a tall empty box with a render
       lying along the bottom of it. */
    L.appendChild(top);
    h3.classList.add('fx-ms__name');
    L.appendChild(h3);
    var figs = el('div', 'fx-ms__figs');
    L.appendChild(figs);
    fillFigures(figs, name);

    if (idpop) {
      /* Always open. Four short values and one sentence do not earn a tap. */
      idpop.hidden = false;
      idpop.removeAttribute('hidden');
      idpop.classList.add('is-open', 'fx-ms__id');
      if (!idpop.querySelector('.fx-ms__idh')) {
        idpop.insertBefore(el('h4', 'fx-ms__idh', 'How to identify it'), idpop.firstChild);
      }
      L.appendChild(idpop);
    }
    if (idbtn) idbtn.setAttribute('data-fx-hide', '1');
    if (head) head.setAttribute('data-fx-hide', '1');

    /* right: the decision */
    var rhead = el('div', 'fx-ms__head');
    if (close) rhead.appendChild(close);
    R.appendChild(rhead);

    var scroll = el('div', 'fx-ms__scroll');
    R.appendChild(scroll);

    var yh = yearBlock.querySelector('h4');
    if (yh) { yh.textContent = 'Select your year model to continue'; yh.className = 'fx-ms__q'; }
    /* The VIN hint is removed, not moved. Everybody in this panel is here
       because they could not read a VIN, so an instruction to go and read one
       is the wrong sentence in the wrong place. */
    var yp = yearBlock.querySelector('p');
    if (yp) yp.setAttribute('data-fx-hide', '1');
    yearBlock.classList.add('fx-ms__year');
    scroll.appendChild(yearBlock);

    var foot = el('div', 'fx-ms__foot');
    var what = el('span', 'fx-ms__what');
    what.appendChild(document.createTextNode('Your selection'));
    what.appendChild(el('b', 'fx-ms__whatb', name));
    foot.appendChild(what);
    var acts = el('span', 'fx-ms__acts');
    if (quiet) acts.appendChild(quiet);
    if (primary) acts.appendChild(primary);
    foot.appendChild(acts);
    R.appendChild(foot);

    sheet.insertBefore(grid, sheet.firstChild);

    /* Anything left of an earlier rebuild goes, or an empty grid stacks up
       behind this one every time the sheet is opened. */
    var stale = sheet.querySelectorAll('.fx-ms');
    for (var i = 0; i < stale.length; i++) {
      if (stale[i] !== grid && stale[i].parentNode) stale[i].parentNode.removeChild(stale[i]);
    }

    sheet.setAttribute('data-fx-ms', '1');

    buildYears(yearBlock);
    adoptSpecs(sheet);
    wireAcc(sheet);
    syncFoot(sheet);
  }

  /* The footer names the trailer AND its chosen year, so somebody three
     accordions down still knows what they are about to act on. It is hidden on
     a phone in fx-sheet.css.

     THE COMPARISON IS NOT A MICRO-OPTIMISATION. This runs on every tick, and a
     bare assignment to textContent replaces the text node whether or not the
     string changed, which is a childList record on the body. fx-ext-vinchip.js
     is listening for exactly that and re-measures and re-sizes the VIN
     selector row when it hears one. Writing unconditionally here put that
     whole measure pass on an 800ms loop. */
  function syncFoot(sheet) {
    var b = sheet.querySelector('.fx-ms__whatb');
    var name = sheet.querySelector('.fx-ms__name');
    var sel = sheet.querySelector('select.vin-filter-select');
    if (!b || !name) return;
    var y = sel && sel.value && sel.value !== 'any' ? sel.value + ' ' : '';
    var next = y + (name.textContent || '').trim();
    if (b.textContent !== next) b.textContent = next;
  }

  /* Built means the grid is there AND still holds the render and the year
     block. A re-render leaves the attribute behind and takes everything else. */
  function intact(sheet) {
    return !!(sheet.querySelector('.fx-ms > .fx-ms__l .ident-sheet-top') &&
              sheet.querySelector('.fx-ms > .fx-ms__r .fx-ms__scroll .ident-sheet-year'));
  }

  function tick() {
    frame = 0;
    var sheets = document.querySelectorAll('.ident-sheet');
    for (var i = 0; i < sheets.length; i++) {
      var s = sheets[i];
      if (s.getAttribute('data-fx-ms') && intact(s)) {
        var yb = s.querySelector('.ident-sheet-year');
        if (yb) buildYears(yb);
        adoptSpecs(s);
        wireAcc(s);
        syncFoot(s);
      } else {
        s.removeAttribute('data-fx-ms');
        build(s);
      }
    }
  }

  function schedule() { if (!frame) frame = setTimeout(tick, 16); }

  function start() {
    tick();
    new MutationObserver(schedule).observe(document.body, { childList: true, subtree: true });
    setInterval(tick, 800);
    document.addEventListener('change', function (e) {
      if (e.target && e.target.classList && e.target.classList.contains('vin-filter-select')) schedule();
    }, true);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();